import { z } from 'zod';

// ---------------------------------------------------------------------------
// Import / migrate: ingest a client's existing documents, extract GRC-relevant
// content with AI assistance and build the shared corpus. See docs/CONTRACTS.md.
// ---------------------------------------------------------------------------

export const ImportMatchSchema = z.object({
  /** Corpus item key the extracted content was mapped to. */
  corpusKey: z.string(),
  title: z.string(),
  action: z.enum(['created', 'updated', 'skipped']),
  /** One-line description of what was extracted / why skipped. */
  summary: z.string(),
});
export type ImportMatch = z.infer<typeof ImportMatchSchema>;

export const ImportJobSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  status: z.enum(['succeeded', 'failed']),
  matches: z.array(ImportMatchSchema),
  error: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  processedAt: z.string().nullable(),
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

export const ImportOverviewSchema = z.object({
  jobs: z.array(ImportJobSchema),
  lastImportAt: z.string().nullable(),
  /**
   * Assessments (frameworks) started since the last import — the UI offers a
   * resync so imported corpus data pre-fills their empty responses.
   */
  newAssessmentsSinceImport: z.array(
    z.object({
      assessmentId: z.string(),
      name: z.string(),
      standardName: z.string(),
      versionLabel: z.string(),
      createdAt: z.string(),
    }),
  ),
  /** Cloud drive connectors surface here once SSO apps are registered. */
  connectors: z.array(
    z.object({
      key: z.enum(['onedrive', 'googledrive']),
      label: z.string(),
      available: z.boolean(),
      reason: z.string(),
    }),
  ),
});
export type ImportOverview = z.infer<typeof ImportOverviewSchema>;

export const DeleteImportJobsInputSchema = z.object({
  /** Import job ids to delete — this organisation's jobs only. */
  jobIds: z.array(z.string()).min(1).max(100),
});
export type DeleteImportJobsInput = z.infer<typeof DeleteImportJobsInputSchema>;

export const DeleteImportJobsResultSchema = z.object({
  deleted: z.number().int(),
});
export type DeleteImportJobsResult = z.infer<typeof DeleteImportJobsResultSchema>;

export const ResyncResultSchema = z.object({
  assessmentsScanned: z.number().int(),
  responsesPrefilled: z.number().int(),
});
export type ResyncResult = z.infer<typeof ResyncResultSchema>;

export const GapAnalysisSchema = z.object({
  /** Corpus coverage across every corpus key referenced by active assessments. */
  corpusGaps: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      /** missing = no corpus item; draft = exists but not approved. */
      status: z.enum(['missing', 'draft', 'approved']),
      referencedBy: z.array(z.string()),
    }),
  ),
  assessments: z.array(
    z.object({
      assessmentId: z.string(),
      name: z.string(),
      standardName: z.string(),
      versionLabel: z.string(),
      totalResponses: z.number().int(),
      emptyResponses: z.number().int(),
      missingCorpusKeys: z.array(z.string()),
    }),
  ),
  generatedAt: z.string(),
});
export type GapAnalysis = z.infer<typeof GapAnalysisSchema>;
